import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import api from '../../../../../../api';
import ConfirmDialog from '../../../../../../components/dialogs/ConfirmDialog';
import JsonTable from '../../../../../../components/JsonTable';

const address = (data, prefix) =>
  ['street', 'city', 'state', 'zip_code']
    .map(part => data[`${prefix}_address_${part}`])
    .filter(Boolean)
    .join(', ');

const formattedDate = value =>
  value && moment(value).isValid() ? moment(value).format('lll') : '';

const hasData = data =>
  data && Object.keys(data).length > 0 && data.carrierok_id !== 'dummy_data';

const authorityValue = value => {
  if (value === null || value === undefined || value === '') return '—';
  const normalized = String(value).toLowerCase();
  const active = ['active', 'authorized', 'a', 'yes', 'true'].includes(
    normalized
  );
  return (
    <span
      className={`verification-badge verification-badge-${
        active ? 'success' : 'neutral'
      }`}
    >
      {String(value)}
    </span>
  );
};

const Field = ({ label, value, wide = false }) => (
  <div
    className={`verification-field${wide ? ' verification-field-wide' : ''}`}
  >
    <div className="verification-field-label">{label}</div>
    <div className="verification-field-value">
      {value === 0 ? 0 : value || '—'}
    </div>
  </div>
);

export default function CarrierOkPanel({
  paymentProfile,
  verification = {},
  onRefresh
}) {
  const [carrier, setCarrier] = useState(verification.carrierok || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [synced, setSynced] = useState(false);
  const active = useRef(true);
  const inFlight = useRef(false);
  const fmcsa = verification.fmcsa || {};
  const available = hasData(carrier);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    if (!synced) setCarrier(verification.carrierok || {});
  }, [verification.carrierok, synced]);

  const sync = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setConfirm(false);
    setLoading(true);
    setError('');
    try {
      const result = await api.groups.verificationInfoSyncCarrierOk(
        paymentProfile.id,
        paymentProfile.company_mc,
        paymentProfile.company_dot
      );
      if (active.current) {
        setCarrier(result || {});
        setSynced(true);
        if (onRefresh) onRefresh(result || {});
      }
    } catch (err) {
      if (active.current)
        setError('Unable to refresh Carrier OK data. Please try again.');
    } finally {
      inFlight.current = false;
      if (active.current) setLoading(false);
    }
  };

  const refresh = () => {
    const lastSync = carrier.created && moment(carrier.created);
    if (
      lastSync &&
      lastSync.isValid() &&
      lastSync.isAfter(moment().subtract(10, 'minutes'))
    ) {
      setError(
        'Carrier OK was synced less than 10 minutes ago. Please try again later.'
      );
    } else if (
      lastSync &&
      lastSync.isValid() &&
      lastSync.isAfter(moment().subtract(10, 'days'))
    ) {
      setConfirm(true);
    } else {
      sync();
    }
  };

  return (
    <section className="verification-panel" aria-label="Carrier OK Data">
      <h2 className="verification-panel-title">Carrier OK Data</h2>
      <div className="verification-actions">
        <button
          type="button"
          className="verification-button"
          onClick={refresh}
          disabled={
            loading ||
            !paymentProfile.id ||
            (!paymentProfile.company_mc && !paymentProfile.company_dot)
          }
          title="Save any MC or DOT changes on the profile before refreshing."
        >
          {loading ? 'Refreshing…' : 'Refresh Carrier OK Data'}
        </button>
        <span className="verification-subtitle" role="status">
          {available && formattedDate(carrier.created)
            ? `Last synced ${moment(carrier.created).fromNow()}`
            : available
            ? 'Last sync unavailable'
            : 'No Carrier OK data available'}
        </span>
      </div>
      {error && (
        <p role="alert" className="verification-error">
          {error}
        </p>
      )}
      {!available ? (
        <p className="verification-empty">
          Carrier OK data is unavailable. Open FMCSA Data below to view the
          fallback source.
        </p>
      ) : (
        <div className="verification-fields">
          <Field wide label="Company Name" value={carrier.legal_name} />
          <Field label="MC" value={carrier.docket_number} />
          <Field label="DOT" value={carrier.dot_number} />
          <Field
            wide
            label="Contact Name (first and last)"
            value={carrier.company_contact_primary}
          />
          <Field label="FMCSA phone" value={carrier.telephone_number} />
          <Field label="Mobile phone" value={carrier.cellphone_number} />
          <Field
            wide
            label="Physical Address"
            value={address(carrier, 'physical')}
          />
          <Field
            wide
            label="Mailing Address"
            value={address(carrier, 'mailing')}
          />
          <Field wide label="Email" value={carrier.email_address} />
          <Field
            label="Common Authority Status"
            value={authorityValue(carrier.authority_common)}
          />
          <Field
            label="Contract Authority Status"
            value={authorityValue(carrier.authority_contract)}
          />
          <Field
            wide
            label="Broker Authorized Status"
            value={authorityValue(carrier.authority_broker)}
          />
          <Field
            wide
            label="Date of last update to FMCSA contact data"
            value={carrier.mcs150_date}
          />
          <Field
            label="Address last changed"
            value={carrier.address_last_changed}
          />
          <Field
            label="Contact last changed"
            value={carrier.contact_last_changed}
          />
          <Field label="Name last changed" value={carrier.name_last_changed} />
          <Field
            label="Email last changed"
            value={carrier.email_last_changed}
          />
          <Field
            label="# Of Trucks Observed"
            value={carrier.number_trucks_observed}
          />
          <Field
            label="# Of Trucks Reported"
            value={carrier.number_trucks_reported}
          />
          <Field label="Tax ID" value={carrier.tax_id} />
          <Field
            label="# Of Inspections"
            value={carrier.number_of_inspections}
          />
          <Field
            label="Phone last changed"
            value={carrier.phone_last_changed}
          />
          <Field label="Risk Score" value={carrier.risk_score} />
          <Field
            wide
            label="Created On"
            value={formattedDate(carrier.created)}
          />
        </div>
      )}
      <details className="verification-accordion verification-fmcsa">
        <summary className="verification-accordion-summary">
          <span>
            <strong>FMCSA Data</strong>
            <span className="verification-subtitle">
              Fallback source · used when Carrier OK is unavailable
            </span>
          </span>
        </summary>
        {Object.keys(fmcsa).length ? (
          <div className="verification-fields">
            <Field wide label="Company Name" value={fmcsa.legal_name} />
            <Field wide label="DBA" value={fmcsa.dba} />
            <Field label="MC" value={fmcsa.mc} />
            <Field label="DOT" value={fmcsa.dot} />
            <Field wide label="Company Type" value={fmcsa.entity_type} />
            <Field
              wide
              label="Physical Address"
              value={address(fmcsa, 'physical')}
            />
            <Field
              wide
              label="Mailing Address"
              value={address(fmcsa, 'mailing')}
            />
            <Field wide label="Phone" value={fmcsa.phone} />
            <Field
              label="Common Authority Status"
              value={authorityValue(fmcsa.has_common_authority_status)}
            />
            <Field
              label="Broker Authority Status"
              value={authorityValue(fmcsa.has_broker_authority_status)}
            />
            <Field
              wide
              label="Operating Status"
              value={fmcsa.operating_status}
            />
            <Field label="Power Units" value={fmcsa.power_units} />
            <Field label="Drivers" value={fmcsa.drivers} />
            <Field
              wide
              label="Date of last update to FMCSA contact data"
              value={fmcsa.mc_150}
            />
          </div>
        ) : (
          <p className="verification-empty">No FMCSA data available.</p>
        )}
      </details>
      {available && (
        <details className="verification-accordion">
          <summary className="verification-accordion-summary">
            Additional Carrier OK data
          </summary>
          <JsonTable
            data={carrier.risk_factors || {}}
            title="CarrierOK Risk Factors"
          />
          <JsonTable data={carrier} title="CarrierOK all data" />
        </details>
      )}
      <ConfirmDialog
        open={confirm}
        message="Last sync was less than 10 days ago. Are you sure you want to proceed?"
        handleConfirm={sync}
        handleExit={() => setConfirm(false)}
        isSubmitting={loading}
      />
    </section>
  );
}
